import * as React from 'react';
import { useSpring, animated, SpringConfig } from 'react-spring';
import { useLast, useDimensions } from '../../utils/hooks';
import Container from './Collapsible.style';

export interface ICollapsibleProps {
  /**
   * boolean value to expand content overriding internal expand state. Include onClick function inside of trigger when using this prop
   */
  expand?: boolean;
  /**
   * element that triggers expand/collapse
   */
  trigger?: React.ReactNode;
  /**
   * boolean value to to determine default state of collapsible content. Overridden by expanded prop
   *
   * @default false
   */
  defaultExpanded?: boolean;
  /**
   * Memo requires children to be specified because memoized components do not
   * automatically inject children as props
   */
  children: React.ReactNode;
  /**
   * Configuration object for react spring
   */
  springConfig?: SpringConfig;
}

const Collapsible: React.FC<ICollapsibleProps> = ({
  expand,
  trigger,
  defaultExpanded = false,
  springConfig = { mass: 1, tension: 265, friction: 25, clamp: true },
  ...rest
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const isExpanded = typeof expand !== 'undefined' ? expand : expanded;
  const last = useLast(isExpanded);
  const [bind, { height: viewHeight }] = useDimensions<HTMLDivElement>();
  const { height, opacity } = useSpring({
    config: springConfig,
    from: { height: 0, opacity: 0 },
    to: {
      height: isExpanded ? viewHeight : 0,
      opacity: isExpanded ? 1 : 0
    }
  }) as any;

  return (
    <>
      {React.isValidElement(trigger) &&
        React.cloneElement(
          trigger,
          {
            'aria-expanded': isExpanded,
            onClick: () => {
              setExpanded(!isExpanded);
            },
            style: { cursor: 'pointer' },
            ...trigger.props
          },
          trigger.props.children
        )}
      <Container
        style={{
          opacity,
          height: isExpanded && last === isExpanded ? 'auto' : height
        }}
      >
        <animated.div {...bind} style={{ overflow: 'auto' }}>
          {rest.children}
        </animated.div>
      </Container>
    </>
  );
};

Collapsible.displayName = 'Collapsible';

export default React.memo(Collapsible);
